import { ChevronUpIcon } from "@components/Icons";
import { PaginationComponent } from "@components/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, ITEMS_PER_PAGE_KEY } from "@constants/table";
import { PermissionContext } from "@contexts/PermissionContext";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import {
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import CustomRadio from "../Forms/CustomRadio";
import TrashIcon from "../Icons/TrashIcon";
import UserNameWithId from "@components/UI/UserNameWithId";

/** FossID (source), Clarity (binary), or Clarity AIR follow-up payload shapes */
const FULFILLMENT_FOLLOWUP_FIELD_KEYS = {
  fossid: {
    resultsKey: "sourceCodeScanFollowupResults",
    resultSeqKey: "sourceCodeScanFollowupResultSeq",
    planSeqKey: "sourceCodeScanFollowupPlanSeq",
  },
  clarity_air: {
    resultsKey: "clarityAirScanFollowupResults",
    resultSeqKey: "clarityAirScanFollowupResultSeq",
    planSeqKey: "clarityAirScanFollowupPlanSeq",
  },
  binary: {
    resultsKey: "binaryScanFollowupResults",
    resultSeqKey: "binaryScanFollowupResultSeq",
    planSeqKey: "binaryScanFollowupPlanSeq",
  },
};

function getFulfillmentFollowupKind(fulfillmentData) {
  if (!fulfillmentData?.length) return "binary";
  const first = fulfillmentData[0];
  if (first.sourceCodeScanFollowupResults !== undefined) return "fossid";
  if (first.clarityAirScanFollowupResults !== undefined) return "clarity_air";
  return "binary";
}

const FulfillmentInfo = forwardRef(
  (
    {
      fulfillmentData = [],
      verificationStatus = "",
      verificationData = {},
      fulfillmentComment,
      setFulfillmentComment,
      loggedInDisplayName,
      projectManagerId,
      "data-testid": dataTestId = "fulfillment-info",
    },
    ref,
  ) => {
    const { colors } = useTheme();
    const { t } = useTranslation();
    const { canManageProjectWorkflow } = useContext(PermissionContext);
    const canManageConditions = canManageProjectWorkflow(projectManagerId);

    // One items-per-page preference shared by every fulfillment section
    const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(
      ITEMS_PER_PAGE_KEY.FULFILLMENT_INFO_LIST,
      DEFAULT_ITEMS_PER_PAGE,
    );

    const [isFulfillmentCollapsed, setIsFulfillmentCollapsed] = useState(false);
    const [fulfillmentEntries, setFulfillmentEntries] = useState([]);

    // States for the modification accordion section
    const [modificationEntries, setModificationEntries] = useState([]);

    // States for the notice accordion section
    const [noticeEntries, setNoticeEntries] = useState([]);

    // States for the patent accordion section
    const [patentEntries, setPatentEntries] = useState([]);

    // States for the direct input fulfillment
    const [directInputEntries, setDirectInputEntries] = useState([]);

    // States for the conflict section
    const [conflictEntries, setConflictEntries] = useState([]);

    // States for fulfillment file and comment
    const [fulfillmentFile, setFulfillmentFile] = useState(null);
    const [localComment, setLocalComment] = useState("");

    // Check if edit mode is enabled based on verification status
    const isReviewStep = verificationStatus === "ON_REVIEW";
    const isFulfillmentStep = verificationStatus === "STANDBY_FOLLOWUP";
    const isCheckFulfillmentStep = verificationStatus === "FOLLOWUP_CHECK";
    const isDoneStep = verificationStatus === "DONE";

    const followupKind = getFulfillmentFollowupKind(fulfillmentData);
    const fk = FULFILLMENT_FOLLOWUP_FIELD_KEYS[followupKind];

    useEffect(() => {
      if (fulfillmentData && fulfillmentData.length > 0) {
        // Create temporary arrays to collect items by type
        const conflictItems = [];
        const openItems = [];
        const modificationItems = [];
        const noticeItems = [];
        const patentItems = [];
        const directInputItems = [];

        // Temporary storage for grouping by plan sequence
        const groupedByPlanSeq = {};

        // For global index tracking
        let globalIndex = 0;

        // Track section indices for proper key mapping
        const sectionIndices = {
          conflict: 0,
          open: 0,
          modification: 0,
          notice: 0,
          patent: 0,
          direct_input: 0,
        };

        // Process all items by type
        fulfillmentData.forEach((group) => {
          const resultsArray = group[fk.resultsKey] || [];

          resultsArray.forEach((item) => {
            if (item && group.followupTypeCd) {
              // Mark as original (not cloned) and assign index with common field names
              const originalItem = {
                ...item,
                followupTarget: group.followupTarget,
                isCloned: false,
                index: globalIndex,
                followupFlag: item.followupFlag || "N",
                followupContent: item.followupContent || "",
              };

              originalItem[fk.resultSeqKey] = item[fk.resultSeqKey];
              originalItem[fk.planSeqKey] = item[fk.planSeqKey];
              originalItem.id = item[fk.resultSeqKey];

              const planSeq = item[fk.planSeqKey];
              if (!groupedByPlanSeq[planSeq]) {
                groupedByPlanSeq[planSeq] = [];
              }
              groupedByPlanSeq[planSeq].push({
                item: originalItem,
                type: group.followupTypeCd,
                globalIndex: globalIndex++,
              });
            }
          });
        });

        // Process grouped items - keep first (smallest result sequence) in main arrays, others in addedRows
        Object.keys(groupedByPlanSeq).forEach((planSeq) => {
          const entries = groupedByPlanSeq[planSeq];

          if (entries.length > 0) {
            // Sort by result sequence to find the smallest (original)
            entries.sort((a, b) => {
              const aSeq = a.item[fk.resultSeqKey];
              const bSeq = b.item[fk.resultSeqKey];
              return aSeq - bSeq;
            });

            // First entry goes to main arrays
            const firstEntry = entries[0];
            switch (firstEntry.type) {
              case "CONFLICT":
                conflictItems.push(firstEntry.item);
                break;
              case "OPEN":
                openItems.push(firstEntry.item);
                break;
              case "MODIFICATION":
                modificationItems.push(firstEntry.item);
                break;
              case "NOTICE":
                noticeItems.push(firstEntry.item);
                break;
              case "PATENT":
                patentItems.push(firstEntry.item);
                break;
              case "DIRECT_INPUT":
                directInputItems.push(firstEntry.item);
                break;
            }

            // Other entries go to addedRows only if they have resultSeq = 0 (indicating they're newly added)
            if (entries.length > 1) {
              const sectionType = firstEntry.type.toLowerCase();
              // Use the current section index for this item
              const currentIndex = sectionIndices[sectionType];
              const addedRowsKey = `${sectionType}-${currentIndex}`;
              const addedRowKeys = [];

              // Collect all updates first to avoid multiple state updates
              const newAddedRowValues = {};
              const newFulfillmentStatusValues = {};
              const newAddedRowCreaterNames = {};

              entries.slice(1).forEach((entry, idx) => {
                const resultSeq = entry.item[fk.resultSeqKey];

                // All rows after the first one should be treated as added rows, regardless of resultSeq
                // This ensures that rows added by the user remain as "added rows" even after backend assigns real IDs
                const rowKey = `initial-${planSeq}-${idx}`;
                addedRowKeys.push(rowKey);

                // Store content, status, and the original result sequence
                newAddedRowValues[`${addedRowsKey}-${rowKey}`] = entry.item.followupContent || "";
                newFulfillmentStatusValues[`${addedRowsKey}-${rowKey}`] = entry.item.followupFlag || "N";
                newAddedRowCreaterNames[`${addedRowsKey}-${rowKey}`] = {
                  name: entry.item.createrName || "",
                  id: entry.item.createrId || "",
                };
                // Store the original result sequence for API calls
                const resultSeqKey = `${addedRowsKey}-${rowKey}-resultSeq`;
                newAddedRowValues[resultSeqKey] = resultSeq;
              });

              // Only update added rows states if there are actual added rows
              if (addedRowKeys.length > 0) {
                setAddedRowValues((prev) => ({
                  ...prev,
                  ...newAddedRowValues,
                }));

                setFulfillmentStatusValues((prev) => ({
                  ...prev,
                  ...newFulfillmentStatusValues,
                }));

                setAddedRowCreaterNames((prev) => ({
                  ...prev,
                  ...newAddedRowCreaterNames,
                }));

                setAddedRows((prev) => ({
                  ...prev,
                  [addedRowsKey]: addedRowKeys,
                }));
              }

              // Increment the section index for the next item in this section
              sectionIndices[sectionType]++;
            }
          }
        });

        // Update state only if there are items to add
        if (conflictItems.length > 0) setConflictEntries(conflictItems);
        if (openItems.length > 0) setFulfillmentEntries(openItems);
        if (modificationItems.length > 0) setModificationEntries(modificationItems);
        if (noticeItems.length > 0) setNoticeEntries(noticeItems);
        if (patentItems.length > 0) setPatentEntries(patentItems);
        if (directInputItems.length > 0) setDirectInputEntries(directInputItems);
      }
    }, [fulfillmentData, followupKind]);

    useEffect(() => {
      if (fulfillmentComment !== undefined) {
        setLocalComment(fulfillmentComment);
      }
    }, [fulfillmentComment]);

    // Handle updating entry fields
    const handleEntryChange = useCallback((id, field, value, entryType) => {
      let setEntries;

      switch (entryType) {
        case "open":
          setEntries = setFulfillmentEntries;
          break;
        case "modification":
          setEntries = setModificationEntries;
          break;
        case "notice":
          setEntries = setNoticeEntries;
          break;
        case "patent":
          setEntries = setPatentEntries;
          break;
        case "conflict":
          setEntries = setConflictEntries;
          break;
        case "directInput":
          setEntries = setDirectInputEntries;
          break;
        default:
          return;
      }

      setEntries((prevEntries) => prevEntries.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
    }, []);

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
      getFulfillmentData: () => {
        // Collect all entries
        const allEntries = [
          ...fulfillmentEntries,
          ...modificationEntries,
          ...noticeEntries,
          ...patentEntries,
          ...conflictEntries,
          ...directInputEntries,
        ];

        // Add entries from added rows
        Object.keys(addedRows).forEach((uniqueKey) => {
          const [sectionType, index] = uniqueKey.split("-");

          // Find the original item based on section type and index
          let originalItem = null;
          switch (sectionType) {
            case "conflict":
              originalItem = conflictEntries[parseInt(index)];
              break;
            case "open":
              originalItem = fulfillmentEntries[parseInt(index)];
              break;
            case "modification":
              originalItem = modificationEntries[parseInt(index)];
              break;
            case "notice":
              originalItem = noticeEntries[parseInt(index)];
              break;
            case "patent":
              originalItem = patentEntries[parseInt(index)];
              break;
            case "direct_input":
              originalItem = directInputEntries[parseInt(index)];
              break;
          }

          if (originalItem) {
            addedRows[uniqueKey].forEach((rowKey) => {
              const addedValue = addedRowValues[`${uniqueKey}-${rowKey}`];
              const normalizedAddedValue = typeof addedValue === "string" ? addedValue.trim() : addedValue;
              if (normalizedAddedValue) {

                // Get the stored result sequence, or use 0 if not found (for truly new rows)
                const storedResultSeq = addedRowValues[`${uniqueKey}-${rowKey}-resultSeq`];
                const resultSeq = storedResultSeq !== undefined ? storedResultSeq : 0;

                // Create a new entry for the added row with unique identifiers
                const addedEntry = {
                  ...originalItem,
                  // Use the stored result sequence if available, otherwise 0 for new entries
                  [fk.resultSeqKey]: resultSeq,
                  [fk.planSeqKey]: originalItem[fk.planSeqKey],
                  followupContent: normalizedAddedValue,
                  followupFlag: fulfillmentStatusValues[`${sectionType}-${index}-${rowKey}`] || "N",
                  isCloned: true, // Mark as added/cloned
                  createrName:
                    addedRowCreaterNames[`${uniqueKey}-${rowKey}`]?.name ||
                    loggedInDisplayName?.userName ||
                    "",
                  createrId:
                    addedRowCreaterNames[`${uniqueKey}-${rowKey}`]?.id ||
                    loggedInDisplayName?.userId ||
                    "",
                  // Ensure this is treated as a new entry, not a modification
                  followupTypeCd: originalItem.followupTypeCd, // Keep the same type as parent
                };
                allEntries.push(addedEntry);
              }
            });
          }
        });

        return allEntries.map((entry) => {
          const { followupTarget, createrId, isCloned, index, ...rest } = entry;
          return {
            ...rest,
            followupContent:
              typeof rest.followupContent === "string" ? rest.followupContent.trim() : rest.followupContent,
          };
        });
      },
      /**
       * Returns the selected fulfillment file
       * @returns {File|null} The selected file or null if no file selected
       */
      getFulfillmentFile: () => fulfillmentFile,
      /**
       * Returns the fulfillment comment text
       * @returns {string} The fulfillment comment
       */
      getFulfillmentComment: () => fulfillmentComment,
      /**
       * Creates FormData object with file and comment for API upload
       * @returns {FormData} FormData object ready for API submission
       */
      getFormDataForUpload: () => {
        const formData = new FormData();
        if (fulfillmentFile) {
          formData.append("file", fulfillmentFile);
        }
        formData.append("followupComment", fulfillmentComment);
        return formData;
      },
    }));

    // Track multiple added rows per item index
    const [addedRows, setAddedRows] = useState({});
    // Track input values for added rows
    const [addedRowValues, setAddedRowValues] = useState({});
    // Track radio button values for fulfillment status
    const [fulfillmentStatusValues, setFulfillmentStatusValues] = useState({});
    // Store creator name/id for added rows
    const [addedRowCreaterNames, setAddedRowCreaterNames] = useState({});

    // Handle add condition button click
    const handleAddCondition = (item, sectionType, index) => {
      const uniqueKey = `${sectionType}-${index}`;
      const newKey = Date.now() + Math.random();
      setAddedRows((prev) => {
        const arr = prev[uniqueKey] ? [...prev[uniqueKey]] : [];
        arr.push(newKey); // unique key
        return { ...prev, [uniqueKey]: arr };
      });
      setAddedRowCreaterNames((prev) => ({
        ...prev,
        [`${uniqueKey}-${newKey}`]: {
          name: loggedInDisplayName?.userName || loggedInDisplayName || "",
          id: loggedInDisplayName?.userId || "",
        },
      }));
    };

    // Handle remove condition row
    const handleRemoveCondition = (sectionType, index, rowKey) => {
      const uniqueKey = `${sectionType}-${index}`;
      setAddedRows((prev) => {
        const arr = prev[uniqueKey] ? prev[uniqueKey].filter((k) => k !== rowKey) : [];
        return { ...prev, [uniqueKey]: arr };
      });
      setAddedRowValues((prev) => {
        const updated = { ...prev };
        delete updated[`${uniqueKey}-${rowKey}`];
        delete updated[`${uniqueKey}-${rowKey}-resultSeq`]; // Also clean up stored result sequence
        return updated;
      });
      // Also clean up fulfillment status values
      setFulfillmentStatusValues((prev) => {
        const updated = { ...prev };
        delete updated[`${uniqueKey}-${rowKey}`];
        return updated;
      });
      setAddedRowCreaterNames((prev) => {
        const updated = { ...prev };
        delete updated[`${uniqueKey}-${rowKey}`];
        return updated;
      });
    };

    // Handle input change for added rows
    const handleAddedInputChange = useCallback((sectionType, index, rowKey, e) => {
      const uniqueKey = `${sectionType}-${index}`;
      setAddedRowValues((prev) => ({ ...prev, [`${uniqueKey}-${rowKey}`]: e.target.value }));
    }, []);

    return (
      <div className={`${colors.bgPrimary} rounded-xl p-4`} data-testid={dataTestId}>
        <div
          data-testid={`${dataTestId}-toggle`}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsFulfillmentCollapsed(!isFulfillmentCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsFulfillmentCollapsed(!isFulfillmentCollapsed);
            }
          }}
          aria-label={isFulfillmentCollapsed ? "Expand Fulfillment Information" : "Collapse Fulfillment Information"}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isFulfillmentCollapsed ? "rotate-180" : ""}`}
            data-testid={`${dataTestId}-chevron`}
          />
          <h2 className={`text-lg font-semibold ${colors.textPrimary} uppercase`} data-testid={`${dataTestId}-title`}>
            {t("project.fulfillment.fulfillment-info")}
          </h2>
        </div>

        <div className={`space-y-4 ${isFulfillmentCollapsed ? "hidden" : ""}`}>
          {conflictEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.conflict-component-list")}
              items={conflictEntries}
              count={conflictEntries.length}
              sectionType="conflict"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          {fulfillmentEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.disclosed-component-list")}
              items={fulfillmentEntries}
              count={fulfillmentEntries.length}
              sectionType="open"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          {modificationEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.modification-component-list")}
              items={modificationEntries}
              count={modificationEntries.length}
              sectionType="modification"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          {noticeEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.notice-component-list")}
              items={noticeEntries}
              count={noticeEntries.length}
              sectionType="notice"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          {patentEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.patent-component-list")}
              items={patentEntries}
              count={patentEntries.length}
              sectionType="patent"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
          {directInputEntries.length > 0 && (
            <FulfillmentSection
              title={t("project.fulfillment.direct-input-fulfillment")}
              items={directInputEntries}
              count={directInputEntries.length}
              sectionType="direct_input"
              handleAddedInputChange={handleAddedInputChange}
              handleEntryChange={handleEntryChange}
              handleAddCondition={handleAddCondition}
              handleRemoveCondition={handleRemoveCondition}
              setFulfillmentStatusValues={setFulfillmentStatusValues}
              addedRowValues={addedRowValues}
              addedRowCreaterNames={addedRowCreaterNames}
              addedRows={addedRows}
              isReviewStep={isReviewStep}
              isFulfillmentStep={isFulfillmentStep}
              isCheckFulfillmentStep={isCheckFulfillmentStep}
              isDoneStep={isDoneStep}
              fulfillmentStatusValues={fulfillmentStatusValues}
              loggedInDisplayName={loggedInDisplayName}
              canManageConditions={canManageConditions}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>
    );
  },
);

// Component to render text with tooltip only when text is truncated
const TruncatedTextWithTooltip = ({ text, tooltipId, colors }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        requestAnimationFrame(() => {
          if (textRef.current) {
            const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTruncated(isOverflowing);
          }
        });
      }
    };

    const timeoutId = setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text]);

  if (!text || text === "-") {
    return <span className={`text-sm ${colors.textSecondary} flex-1 truncate`}>{text || "-"}</span>;
  }

  return (
    <>
      <span
        ref={textRef}
        className={`text-sm ${colors.textSecondary} flex-1 truncate`}
        style={{ maxWidth: "100%" }}
        {...(isTruncated
          ? {
              "data-tooltip-id": tooltipId,
              "data-tooltip-content": text,
            }
          : {})}
      >
        {text}
      </span>
      {isTruncated && (
        <Tooltip
          id={tooltipId}
          place="top"
          className={`${colors.bgTooltip} ${colors.textTooltip} !text-xs !rounded-lg !px-3 !py-2 !max-w-[500px] !z-50`}
          style={{
            backgroundColor: colors.headerTooltipBackgroundColor,
            color: colors.headerTooltipTextColor,
            fontSize: "12px",
            borderRadius: "8px",
            padding: "8px 12px",
            maxWidth: "500px",
            minWidth: "200px",
            zIndex: 50,
            wordWrap: "break-word",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        />
      )}
    </>
  );
};

// FulfillmentSection component definition
const FulfillmentSection = ({
  title,
  items,
  count,
  sectionType,
  isReviewStep,
  isFulfillmentStep,
  isCheckFulfillmentStep,
  isDoneStep,
  fulfillmentStatusValues,
  handleEntryChange,
  handleAddedInputChange,
  handleRemoveCondition,
  handleAddCondition,
  addedRowValues,
  addedRowCreaterNames,
  addedRows,
  setFulfillmentStatusValues,
  loggedInDisplayName = "",
  canManageConditions = false,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  onItemsPerPageChange,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const tableWrapperRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!tableWrapperRef.current) return;

    const updateWidth = (width) => {
      if (width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth(tableWrapperRef.current.offsetWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        updateWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(tableWrapperRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const getInitialWidths = (width) => {
    const hasStatusColumn = isFulfillmentStep || isCheckFulfillmentStep || isDoneStep;

    if (hasStatusColumn) {
      return {
        0: width * 0.2,
        1: width * 0.45,
        2: width * 0.15,
        3: width * 0.2,
      };
    }

    return {
      0: width * 0.25,
      1: width * 0.6,
      2: width * 0.15,
    };
  };

  const [columnWidths, setColumnWidths] = useState({});

  useLayoutEffect(() => {
    if (!containerWidth) return;

    setColumnWidths(getInitialWidths(containerWidth));
  }, [containerWidth, isFulfillmentStep, isCheckFulfillmentStep, isDoneStep]);

  const [resizingColumn, setResizingColumn] = useState(null);

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();

    const th = e.target.closest("th");
    if (!th) return;

    const startWidth = th.offsetWidth;

    setResizingColumn({
      index: columnIndex,
      startX: e.clientX,
      startWidth,
    });

    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const handleResizeMove = (e) => {
    if (!resizingColumn) return;

    const diff = e.clientX - resizingColumn.startX;

    const newWidth = Math.max(80, resizingColumn.startWidth + diff);

    setColumnWidths((prev) => ({
      ...prev,
      [resizingColumn.index]: newWidth,
    }));
  };

  const handleResizeEnd = () => {
    if (!resizingColumn) return;

    setResizingColumn(null);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };

  useEffect(() => {
    if (!resizingColumn) return;

    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", handleResizeEnd);

    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [resizingColumn]);

  // Render component column
  const renderComponent = (item) => {
    // Extract component info from followupTarget or component fields
    let componentName = "";
    let componentVersion = "";
    let license = "";

    if (item.followupTarget) {
      // Parse followupTarget format: "component version(license)"
      // Example: "itextsharp 5.5.10(AGPL-3.0-only)" or "openssl 0.9.8y(OpenSSL)"
      const match = item.followupTarget.match(/^(.+?)\s+([^\s]+)\(([^)]+)\)$/);
      if (match) {
        componentName = match[1].trim();
        componentVersion = match[2].trim();
        license = match[3].trim();
      } else {
        // Fallback: try to extract just the component name and version
        const parts = item.followupTarget.split(" ");
        if (parts.length >= 2) {
          componentName = parts.slice(0, -1).join(" ");
          componentVersion = parts[parts.length - 1];
        } else {
          componentName = item.followupTarget;
        }
      }
    } else {
      // Fallback to component fields
      componentName = item.componentName || "";
      componentVersion = item.comoponentVersion || ""; // Note: API has typo "comoponentVersion"
      license = item.license || "";
    }

    return (
      <td className="px-4 py-2 align-center overflow-hidden">
        <div className="flex flex-col">
          <div
            className={`text-sm font-semibold ${colors.textPrimary} truncate`}
            title={`${componentName} ${componentVersion}`}
          >
            {componentName} {componentVersion && `(${componentVersion})`}
          </div>
          <div className={`text-sm ${colors.textSecondary} truncate`} title={license}>
            {license}
          </div>
        </div>
      </td>
    );
  };

  // Render condition column for original rows
  const renderCondition = useMemo(
    () => (item, index, sectionType) => {
      // Map sectionType to entryType directly
      const entryTypeMap = {
        conflict: "conflict",
        open: "open",
        modification: "modification",
        notice: "notice",
        patent: "patent",
        direct_input: "directInput",
      };
      const entryType = entryTypeMap[sectionType] || sectionType;

      return (
        <td className="px-4 py-2 align-center overflow-hidden">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            {isReviewStep && canManageConditions ? (
              <>
                <input
                  type="text"
                  defaultValue={item.followupContent || ""}
                  onBlur={(e) => {
                    handleEntryChange(item.id, "followupContent", e.target.value, entryType);
                  }}
                  placeholder={t("project.condition.input-placeholder")}
                  maxLength={200}
                  className={`flex-1 w-full border focus:outline-none transition-colors ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.focusBorder}`}
                  style={{
                    height: "2rem",
                    fontSize: "0.875rem",
                    padding: "0px 0.625rem",
                    borderRadius: "0.5rem",
                  }}
                />
                <button
                  onClick={() => handleAddCondition(item, sectionType, index)}
                  className={`inline-flex items-center w-36 whitespace-nowrap px-3 py-1 text-sm font-medium ${colors.textActive} rounded hover:opacity-80`}
                >
                  {t("project.condition.add-condition")}
                </button>
              </>
            ) : (
              <TruncatedTextWithTooltip
                text={item.followupContent}
                tooltipId={`condition-tooltip-${sectionType}-${index}`}
                colors={colors}
              />
            )}
          </div>
        </td>
      );
    },
    [
      isReviewStep,
      canManageConditions,
      colors.textActive,
      colors.textSecondary,
      colors.borderPrimary,
      colors.bgInput,
      colors.textPrimary,
      handleEntryChange,
      handleAddCondition,
      t,
    ],
  );

  // Render added by column for original rows
  const renderAddedBy = (item) => (
    <td className="px-4 py-2 align-center text-left">
      <UserNameWithId name={item.createrName} id={item.createrId} className={colors.textSecondary} />
    </td>
  );

  // Render fulfillment status column
  const renderFulfillmentStatus = useCallback(
    (item, index, sectionType, isAddedRow = false, rowKey = null) => {
      if (!isFulfillmentStep && !isCheckFulfillmentStep && !isDoneStep) return null;

      const uniqueKey = isAddedRow ? `${sectionType}-${index}-${rowKey}` : `${sectionType}-${index}`;

      // Get the current value from local state or original data
      const currentValue = fulfillmentStatusValues[uniqueKey] || item.followupFlag;

      const checkedYes = currentValue === "Y";
      const checkedNo = currentValue === "N" || (!currentValue && !isAddedRow);

      // Map sectionType to entryType directly
      const entryTypeMap = {
        conflict: "conflict",
        open: "open",
        modification: "modification",
        notice: "notice",
        patent: "patent",
        direct_input: "directInput",
      };
      const entryType = entryTypeMap[sectionType] || sectionType;

      // For DONE step, show Pass/Fail text instead of radio buttons
      if (isDoneStep) {
        const statusText =
          currentValue === "Y"
            ? t("project.fulfillment.fulfillment-status-pass")
            : t("project.fulfillment.fulfillment-status-fail");
        const statusColor = currentValue === "Y" ? colors.textSuccess : colors.textDanger;

        return (
          <td className="px-4 py-2 align-center text-left">
            <span className={`text-sm font-semibold ${statusColor}`}>{statusText}</span>
          </td>
        );
      }

      return (
        <td className="px-4 py-2 align-center text-left">
          <div className="flex items-center gap-10">
            <CustomRadio
              name={uniqueKey}
              value="Y"
              checked={checkedYes}
              label={t("common.yes")}
              disabled={isCheckFulfillmentStep}
              onChange={(e) => {
                if (isAddedRow) {
                  // For added rows, update the fulfillmentStatusValues state
                  setFulfillmentStatusValues((prev) => ({
                    ...prev,
                    [uniqueKey]: "Y",
                  }));
                } else {
                  // For original rows, update the main state
                  handleEntryChange(item.id, "followupFlag", "Y", entryType);
                }
              }}
            />
            <CustomRadio
              name={uniqueKey}
              value="N"
              checked={checkedNo}
              label={t("common.no")}
              disabled={isCheckFulfillmentStep}
              onChange={(e) => {
                if (isAddedRow) {
                  // For added rows, update the fulfillmentStatusValues state
                  setFulfillmentStatusValues((prev) => ({
                    ...prev,
                    [uniqueKey]: "N",
                  }));
                } else {
                  // For original rows, update the main state
                  handleEntryChange(item.id, "followupFlag", "N", entryType);
                }
              }}
            />
          </div>
        </td>
      );
    },
    [isFulfillmentStep, isCheckFulfillmentStep, isDoneStep, fulfillmentStatusValues, handleEntryChange],
  );

  // Render condition column for added rows
  const renderAddedCondition = useCallback(
    (item, index, sectionType, rowKey) => (
      <td className="px-4 py-2 align-center">
        <div className="flex items-center gap-2 min-w-0">
          {isReviewStep && canManageConditions ? (
            <>
              <input
                key={`added-input-${sectionType}-${index}-${rowKey}`}
                type="text"
                defaultValue={addedRowValues[`${sectionType}-${index}-${rowKey}`] || ""}
                onBlur={(e) => handleAddedInputChange(sectionType, index, rowKey, e)}
                placeholder={t("project.condition.input-placeholder")}
                maxLength={200}
                className={`flex-1 w-full border focus:outline-none transition-colors ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.focusBorder}`}
                style={{
                  height: "2rem",
                  fontSize: "0.875rem",
                  padding: "0px 0.625rem",
                  borderRadius: "0.5rem",
                }}
              />
              <button
                onClick={() => handleRemoveCondition(sectionType, index, rowKey)}
                className={`flex items-center w-36 whitespace-nowrap px-3 py-1 text-sm font-medium ${colors.iconDelete} rounded hover:opacity-80`}
              >
                <TrashIcon className="w-3 h-3 mr-1 mb-0.5" />
                {t("communication.remove-file")}
              </button>
            </>
          ) : (
            <TruncatedTextWithTooltip
              text={addedRowValues[`${sectionType}-${index}-${rowKey}`]}
              tooltipId={`added-condition-tooltip-${sectionType}-${index}-${rowKey}`}
              colors={colors}
            />
          )}
        </div>
      </td>
    ),
    [
      isReviewStep,
      canManageConditions,
      colors.borderPrimary,
      colors.bgInput,
      colors.textPrimary,
      colors.iconDelete,
      colors.textSecondary,
      addedRowValues,
      handleAddedInputChange,
      handleRemoveCondition,
      t,
    ],
  );

  // Render added by column for added rows
  const renderAddedByEmpty = (item, sectionType, index, rowKey) => {
    const key = `${sectionType}-${index}-${rowKey}`;
    const storedCreator = addedRowCreaterNames[key];
    const fallbackName =
      typeof loggedInDisplayName === "string"
        ? loggedInDisplayName
        : loggedInDisplayName?.userName || "";
    const fallbackId =
      typeof loggedInDisplayName === "object" ? loggedInDisplayName?.userId || "" : "";

    const displayName = storedCreator?.name || fallbackName || "";
    const displayId = storedCreator?.id || fallbackId || "";

    return (
      <td className="px-4 py-2 align-center text-left">
        <UserNameWithId name={displayName} id={displayId} className={colors.textSecondary} />
      </td>
    );
  };

  // Client-side pagination: rows are rendered per page, but every row keeps its
  // absolute index because the condition / status / added-row states are keyed by it.
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = items.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, totalItems]);

  const visibleColumnCount = isFulfillmentStep || isCheckFulfillmentStep || isDoneStep ? 4 : 3;

  const hasMeasuredWidths = containerWidth > 0 && columnWidths[0] != null;

  const fallbackColumnWidths =
    visibleColumnCount === 4 ? { 0: "20%", 1: "45%", 2: "15%", 3: "20%" } : { 0: "25%", 1: "60%", 2: "15%" };

  const resolvedColumnWidths = hasMeasuredWidths ? columnWidths : fallbackColumnWidths;

  const totalWidth = hasMeasuredWidths
    ? Array.from({ length: visibleColumnCount }).reduce((sum, _, i) => sum + (columnWidths[i] || 0), 0)
    : null;

  return (
    <div>
      <div className="flex items-center justify-between pt-4 pb-2">
        <h3 className={`text-base font-semibold ${colors.textPrimary}`}>
          {title} ({count})
        </h3>
      </div>
      <div ref={tableWrapperRef} className={`border ${colors.borderPrimary} rounded-xl overflow-x-auto`}>
        <table className="table-fixed w-full" style={hasMeasuredWidths ? { width: `${totalWidth}px` } : undefined}>
          <colgroup>
            <col style={{ width: resolvedColumnWidths[0] }} />
            <col style={{ width: resolvedColumnWidths[1] }} />
            <col style={{ width: resolvedColumnWidths[2] }} />

            {(isFulfillmentStep || isCheckFulfillmentStep || isDoneStep) && (
              <col style={{ width: resolvedColumnWidths[3] }} />
            )}
          </colgroup>
          <thead>
            <tr>
              <th
                style={{ position: "relative" }}
                className={`text-left px-4 py-3 text-sm font-semibold ${colors.textSecondary} uppercase tracking-wide`}
              >
                <div className="flex flex-col">
                  <div className={`text-sm font-semibold ${colors.textMuted}`}>{t("common.component")}</div>
                  <div className={`text-sm font-semibold ${colors.textActive}`}>{t("common.component-license")}</div>
                </div>
                <div
                  className="absolute right-0 top-0 h-full w-4 cursor-col-resize z-10"
                  onMouseDown={(e) => handleResizeStart(e, 0)}
                  style={{ userSelect: "none" }}
                >
                  <div
                    className={`h-full border-r ${colors.borderPrimary}`}
                    style={{
                      height: "calc(100% - 8px)",
                      margin: "4px 0px",
                    }}
                  />
                </div>
              </th>
              <th
                style={{ position: "relative" }}
                className={`text-left px-4 py-3 text-sm font-semibold ${colors.textSecondary} uppercase tracking-wide`}
              >
                {t("project.fulfillment.condition")}
                <div
                  className="absolute right-0 top-0 h-full w-4 cursor-col-resize z-10"
                  onMouseDown={(e) => handleResizeStart(e, 1)}
                  style={{ userSelect: "none" }}
                >
                  <div
                    className={`h-full border-r ${colors.borderPrimary}`}
                    style={{
                      height: "calc(100% - 8px)",
                      margin: "4px 0px",
                    }}
                  />
                </div>
              </th>
              <th
                style={{ position: "relative" }}
                className={`text-left px-4 py-3 text-sm font-semibold ${colors.textSecondary} uppercase tracking-wide`}
              >
                {t("project.fulfillment.added-by")}
                <div
                  className="absolute right-0 top-0 h-full w-4 cursor-col-resize z-10"
                  onMouseDown={(e) => handleResizeStart(e, 2)}
                  style={{ userSelect: "none" }}
                >
                  <div
                    className={`h-full border-r ${colors.borderPrimary}`}
                    style={{
                      height: "calc(100% - 8px)",
                      margin: "4px 0px",
                    }}
                  />
                </div>
              </th>
              {(isFulfillmentStep || isCheckFulfillmentStep || isDoneStep) && (
                <th
                  style={{ position: "relative" }}
                  className={`text-left px-4 py-3 text-sm font-semibold ${colors.textSecondary} uppercase tracking-wide`}
                >
                  {t("project.fulfillment.fulfillment-status")}
                  <div
                    className="absolute right-0 top-0 h-full w-4 cursor-col-resize z-10"
                    onMouseDown={(e) => handleResizeStart(e, 3)}
                    style={{ userSelect: "none" }}
                  >
                    <div
                      className={`h-full border-r ${colors.borderPrimary}`}
                      style={{
                        height: "calc(100% - 8px)",
                        margin: "4px 0px",
                      }}
                    />
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {(() => {
              let visualRowIndex = 0;
              const getStripeClass = () => {
                const stripeClass = visualRowIndex % 2 !== 1 ? colors.bgFulfillmentStripe : "";
                visualRowIndex += 1;
                return stripeClass;
              };

              return paginatedItems.map((item, pageIndex) => {
                const index = startIndex + pageIndex;
                const extraRows = addedRows[`${sectionType}-${index}`] || [];
                return (
                  <Fragment key={item.id || `item-${index}`}>
                    <tr className={getStripeClass()}>
                      {/* Component Column */}
                      {renderComponent(item)}
                      {/* Condition Column */}
                      {renderCondition(item, index, sectionType)}
                      {/* Added By Column */}
                      {renderAddedBy(item)}
                      {/* Fulfillment Status Column */}
                      {renderFulfillmentStatus(item, index, sectionType)}
                    </tr>
                    {extraRows?.map((rowKey) => (
                      <tr key={`added-${item.id}-${rowKey}`} className={getStripeClass()}>
                        {/* Component Column */}
                        {renderComponent(item)}
                        {/* Condition Column */}
                        {renderAddedCondition(item, index, sectionType, rowKey)}
                        {/* Added By Column */}
                        {renderAddedByEmpty(item, sectionType, index, rowKey)}
                        {/* Fulfillment Status Column */}
                        {renderFulfillmentStatus(item, index, sectionType, true, rowKey)}
                      </tr>
                    ))}
                  </Fragment>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
      <PaginationComponent
        dataTestId={`fulfillment-${sectionType}`}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        totalData={totalItems}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={onItemsPerPageChange}
        shouldShowGoToPage={true}
        showPagination={true}
        showItemsPerPageSelector={true}
        useFixedSelect={false}
        data={items}
      />
    </div>
  );
};

export default FulfillmentInfo;
